import { StrategyConfig, StrategyMode, StrategyValidationContext } from './types';
import {
  scoutListConfig,
  freshFacesConfig,
  referralsConfig,
  notReferringConfig,
  patientJourneyConfig,
  treatingVolumeConfig,
} from './strategies';
import { STRATEGY_TYPES } from '../constants';

/**
 * Strategy type key - derived from STRATEGY_TYPES constant
 */
export type StrategyTypeKey = keyof typeof STRATEGY_TYPES;

/**
 * Strategy Button representation for UI display
 */
export interface StrategyButton {
  label: string;
  value: string;
  description: string;
  icon: StrategyConfig['icon'];
  disabled: boolean;
  disabledReason?: string;
}

/**
 * Strategy Configuration Registry
 *
 * Plain object registry mapping strategy types to their configurations.
 * This follows the same pattern as COMPONENT_REGISTRY and PERSISTED_SLICES.
 *
 * Uses StrategyTypeKey for type-safe keys that match STRATEGY_TYPES.
 */
export const STRATEGY_CONFIGS: Record<StrategyTypeKey, StrategyConfig> = {
  SCOUT_LIST: scoutListConfig,
  FRESH_FACES: freshFacesConfig,
  REFERRALS: referralsConfig,
  NOT_REFERRING: notReferringConfig,
  PATIENT_JOURNEY: patientJourneyConfig,
  TREATING_VOLUME: treatingVolumeConfig,
};

/**
 * Get a strategy configuration by type
 */
export const getStrategyConfig = (
  type: string | null | undefined
): StrategyConfig | undefined => {
  if (!type) return undefined;
  return STRATEGY_CONFIGS[type];
};

/**
 * Check if a strategy type exists in the registry
 */
export const hasStrategyConfig = (type: string): boolean => {
  return type in STRATEGY_CONFIGS;
};

/**
 * Get all registered strategy configurations
 */
export const getAllStrategyConfigs = (): StrategyConfig[] => {
  return Object.values(STRATEGY_CONFIGS);
};

/**
 * Get all strategy configurations for a specific mode (target or engage)
 */
export const getStrategyConfigsByMode = (mode: StrategyMode): StrategyConfig[] => {
  return getAllStrategyConfigs().filter((config) => config.mode === mode);
};

/**
 * Get strategy buttons for UI display, filtered by mode
 * Returns buttons sorted with enabled strategies first
 */
export const getStrategyButtons = (mode: StrategyMode): StrategyButton[] => {
  const configs = getStrategyConfigsByMode(mode);

  const buttons: StrategyButton[] = configs.map((config) => ({
    label: config.label,
    value: config.type,
    description: config.description,
    icon: config.icon,
    disabled: config.disabled,
    disabledReason: config.disabledReason,
  }));

  // Sort: SCOUT_LIST first for target mode, then enabled before disabled
  return buttons.sort((a, b) => {
    // Scout List always comes first for target strategies
    if (mode === 'target') {
      if (a.value === 'SCOUT_LIST') return -1;
      if (b.value === 'SCOUT_LIST') return 1;
    }

    // Then sort by enabled (false) before disabled (true)
    if (a.disabled !== b.disabled) {
      return a.disabled ? 1 : -1;
    }

    // Maintain original registration order
    return 0;
  });
};

/**
 * Validate progress for a strategy type
 * Returns true if the strategy has enough data to show progress
 */
export const validateStrategyProgress = (
  type: string | null | undefined,
  context: StrategyValidationContext
): boolean => {
  const config = getStrategyConfig(type);
  if (!config) return false;
  return config.validateProgress(context);
};

/**
 * Validate if a strategy is complete and can be submitted
 */
export const validateStrategyComplete = (
  type: string | null | undefined,
  context: StrategyValidationContext
): boolean => {
  const config = getStrategyConfig(type);
  if (!config) return false;
  return config.validateComplete(context);
};

/**
 * Get the configuration component for a strategy type
 */
export const getStrategyConfigComponent = (
  type: string | null | undefined
): StrategyConfig['ConfigComponent'] | null => {
  const config = getStrategyConfig(type);
  return config?.ConfigComponent || null;
};

/**
 * Get the label for a strategy type
 */
export const getStrategyLabel = (type: string | null | undefined): string => {
  const config = getStrategyConfig(type);
  return config?.label || type || '';
};

/**
 * Serialize form data for creating a new strategy
 */
export const serializeStrategyForCreate = (
  type: string | null | undefined,
  context: StrategyValidationContext
): ReturnType<StrategyConfig['serializeForCreate']> | null => {
  const config = getStrategyConfig(type);
  if (!config) return null;
  return config.serializeForCreate(context);
};

/**
 * Serialize form data for updating an existing strategy
 */
export const serializeStrategyForUpdate = (
  type: string | null | undefined,
  context: StrategyValidationContext
): ReturnType<StrategyConfig['serializeForUpdate']> | null => {
  const config = getStrategyConfig(type);
  if (!config) return null;
  return config.serializeForUpdate(context);
};
